// src/components/InferenceLogs.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import debounce from 'lodash/debounce';
import './InferenceLogs.css';

const truncateText = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const formatCost = (log) => {
  if (typeof log.cost === 'number' && !Number.isNaN(log.cost)) {
    return `$${log.cost.toFixed(2)}`;
  }
  return log.status === 'completed' ? 'Pending' : 'N/A';
};

const renderPreview = (log) => {
  // Handle different statuses
  if (log.status === 'running') {
    return <span className="status-running">⏳ Running...</span>;
  }
  
  if (log.status === 'connection_error') {
    return <span className="status-connection-error">⚠️ Connection Issue (check FAL)</span>;
  }

  if (log.status === 'content_policy_violation') {
    return <span className="status-content-policy-violation">🚫 Content Flagged</span>;
  }

  if (log.status === 'failed') {
    let errorText = 'Failed';
    if (log.errorDetails?.validationErrors && log.errorDetails.validationErrors.length > 0) {
      // Show the first validation error in a user-friendly way
      const firstError = log.errorDetails.validationErrors[0];
      const field = firstError.loc?.filter(l => l !== 'body').join('.') || 'field';
      errorText = `${field}: ${firstError.msg}`;
    } else if (log.errorDetails?.message) {
      errorText = log.errorDetails.message;
    }
    return <span className="status-failed">❌ {truncateText(errorText, 30)}</span>;
  }
  
  if (!log.result) return 'No result';
  
  try {
    const result = typeof log.result === 'string' ? JSON.parse(log.result) : log.result;
    
    // Check various possible URL locations in the result
    const urlData = result.data?.images?.[0]?.url || // Image array
                   result.data?.video?.url ||        // Video object
                   result.data?.audio?.url ||        // Audio object (assuming similar structure)
                   result.data?.audio_file?.url ||   // New: audio_file object
                   result.data?.url ||               // Direct URL in data
                   result.url;                      // Direct URL in result
      // Handle image results
    if (urlData && (urlData.endsWith('.png') || urlData.endsWith('.jpg') || urlData.endsWith('.jpeg'))) {
      return (
        <a href={urlData} target="_blank" rel="noopener noreferrer">          <img 
            src={urlData} 
            alt="Generated" 
            style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }} 
          />
        </a>
      );
    }
    
    // Handle video results
    if (urlData && (urlData.endsWith('.mp4') || urlData.endsWith('.webm'))) {
      return (        <video 
          controls 
          style={{ maxWidth: '150px', maxHeight: '150px' }}
        >
          <source src={urlData} type={`video/${urlData.split('.').pop()}`} />
          Your browser does not support the video tag.
        </video>
      );
    }
    
    // Handle audio results
    if (urlData && (urlData.endsWith('.mp3') || urlData.endsWith('.wav'))) {
      return (
        <audio controls>
          <source src={urlData} type={`audio/${urlData.split('.').pop()}`} />
          Your browser does not support the audio element.
        </audio>
      );
    }
    
    // For text/JSON results, show a preview of the data or URL
    if (urlData) {
      return <a href={urlData} target="_blank" rel="noopener noreferrer">{truncateText(urlData, 50)}</a>;
    }
    
    // Default to text preview
    return truncateText(JSON.stringify(result.data || result), 50);
  } catch (error) {
    console.error('Error parsing result:', error);
    return 'Invalid result format';
  }
};

const isLikelyFileUrl = (value) => typeof value === 'string' && /^(https?:|data:)/i.test(value);

const getMediaKind = (url) => {
  const clean = url.split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|webp|avif|heic|heif|gif|bmp|svg)$/.test(clean)) return 'image';
  if (/\.(mp4|webm|mov|avi|mkv)$/.test(clean)) return 'video';
  if (/\.(mp3|wav|flac|ogg|m4a|aac)$/.test(clean)) return 'audio';
  if (/\.pdf$/.test(clean)) return 'pdf';
  return 'unknown';
};

// Pulls out every file/URL-shaped input value (single or array fields) so the
// details view can preview them as thumbnails instead of leaving them buried
// in the raw JSON dump. Falls back to a name/shape-based guess when the
// model's input_schema isn't available (e.g. an older log for a deleted model).
const getInputFileEntries = (inputs, schema) => {
  if (!inputs || typeof inputs !== 'object') return [];

  const entries = [];
  Object.entries(inputs).forEach(([key, value]) => {
    const fieldSchema = schema?.[key];
    const values = Array.isArray(value) ? value : [value];

    const isSchemaFileField = fieldSchema
      ? fieldSchema.fieldType === 'file' || fieldSchema.fieldType === 'file-array'
      : false;
    const isNameHeuristicFileField = !fieldSchema &&
      /url/i.test(key) &&
      values.some(isLikelyFileUrl);

    if (!isSchemaFileField && !isNameHeuristicFileField) return;

    values.forEach((url, index) => {
      if (!isLikelyFileUrl(url)) return;
      entries.push({
        field: key,
        label: fieldSchema?.title || key,
        url,
        index: values.length > 1 ? index : null,
        kind: getMediaKind(url)
      });
    });
  });

  return entries;
};

const InputThumbnails = ({ inputs, schema }) => {
  const entries = getInputFileEntries(inputs, schema);
  if (entries.length === 0) return null;

  return (
    <>
      <h4>Input Files</h4>
      <div className="input-thumbnails">
        {entries.map((entry, i) => (
          <a
            key={`${entry.field}-${i}`}
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
            className="input-thumbnail-item"
            title={entry.url}
          >
            {entry.kind === 'image' && (
              <img src={entry.url} alt={entry.label} className="input-thumbnail" />
            )}
            {entry.kind === 'video' && (
              <video src={entry.url} className="input-thumbnail-video" muted />
            )}
            {entry.kind === 'audio' && (
              <audio src={entry.url} controls className="input-thumbnail-audio" />
            )}
            {(entry.kind === 'pdf' || entry.kind === 'unknown') && (
              <div className="input-thumbnail-file">📄</div>
            )}
            <span className="input-thumbnail-label">
              {entry.label}{entry.index !== null ? ` #${entry.index + 1}` : ''}
            </span>
          </a>
        ))}
      </div>
    </>
  );
};

const PaginationControls = ({ pagination, onPageChange }) => (
  <div className="pagination">
    <button
      onClick={() => onPageChange(pagination.page - 1)}
      disabled={pagination.page === 1}
    >
      Previous
    </button>
    <span>
      Page {pagination.page} of {pagination.totalPages}
    </span>
    <button
      onClick={() => onPageChange(pagination.page + 1)}
      disabled={pagination.page === pagination.totalPages}
    >
      Next
    </button>
  </div>
);

const InferenceLogs = () => {
  const [logs, setLogs] = useState([]);
  const [models, setModels] = useState({});
  const [modelSchemas, setModelSchemas] = useState({});
  const [requesters, setRequesters] = useState([]);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [filterModel, setFilterModel] = useState('all');
  const [filterRequester, setFilterRequester] = useState('all');
  const [searchPrompt, setSearchPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  });

  const fetchLogs = useCallback(async (page = 1, currentSearch = searchPrompt) => {
    setIsLoading(true);
    setError(null);
    const currentPageSize = pagination.pageSize;
    try {
      const response = await api.get('/api/logs', {
        params: {
          page,
          pageSize: currentPageSize,
          modelId: filterModel !== 'all' ? filterModel : undefined,
          requester: filterRequester !== 'all' ? filterRequester : undefined,
          search: currentSearch || undefined
        }
      });
      
      const responseData = Array.isArray(response.data) ? response.data : response.data.data;
      const paginationData = Array.isArray(response.data) ? {
        total: response.data.length,
        page: 1,
        pageSize: response.data.length,
        totalPages: 1
      } : response.data.pagination;
      
      if (!Array.isArray(responseData)) {
        console.error('Invalid data format:', response.data);
        setError('Invalid data format from server');
        setLogs([]);
        return;
      }
        const validLogs = responseData.map(log => {
        try {          const inputs = typeof log.inputs === 'string' ? JSON.parse(log.inputs) : log.inputs;
          return {            
            _id: log.id,
            modelId: log.modelId,
            prompt: inputs?.prompt || inputs?.text || '',
            inputs: inputs,
            result: log.result ? (typeof log.result === 'string' ? JSON.parse(log.result) : log.result) : null,
            status: log.status || 'completed',
            errorDetails: log.errorDetails ? (typeof log.errorDetails === 'string' ? JSON.parse(log.errorDetails) : log.errorDetails) : null,
            sessionId: log.sessionId,
            falRequestId: log.falRequestId,
            requester: log.requester,
            ipAddress: log.ipAddress,
            cost: log.cost !== undefined && log.cost !== null ? parseFloat(log.cost) : null,
            startedAt: log.startedAt,
            completedAt: log.completedAt,
            createdAt: log.createdAt
          };
        } catch (err) {
          console.error('Error parsing log data:', err, log);
          return {            _id: log.id,
            modelId: log.modelId,
            prompt: 'Error parsing log data',
            result: log.result,
            status: log.status || 'unknown',
            errorDetails: log.errorDetails,
            sessionId: log.sessionId,
            requester: log.requester,
            ipAddress: log.ipAddress,
            cost: log.cost !== undefined && log.cost !== null ? parseFloat(log.cost) : null,
            startedAt: log.startedAt,
            completedAt: log.completedAt,
            createdAt: log.createdAt
          };
        }
      });

      setLogs(validLogs);
      
      if (paginationData) {
        setPagination(prev => ({
          ...prev,
          ...paginationData
        }));
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      setError('Failed to fetch logs. Please try again later.');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [filterModel, filterRequester, pagination.pageSize, searchPrompt]);

  const fetchModels = async () => {
    try {
      const response = await api.get('/api/models');
      // Sort models alphabetically by name
      const sortedModels = response.data.sort((a, b) => a.name.localeCompare(b.name));
      const modelMap = {};
      const schemaMap = {};
      sortedModels.forEach(model => {
        modelMap[model.id.toString()] = model.name;
        try {
          schemaMap[model.id.toString()] = typeof model.input_schema === 'string'
            ? JSON.parse(model.input_schema)
            : (model.input_schema || {});
        } catch (err) {
          schemaMap[model.id.toString()] = {};
        }
      });
      setModels(modelMap);
      setModelSchemas(schemaMap);
    } catch (error) {
      console.error('Error fetching models:', error);
      // Don't set error state here as it's not critical
    }
  };

  const fetchRequesters = async () => {
    try {
      const response = await api.get('/api/logs/requesters');
      setRequesters(response.data.data || []);
    } catch (error) {
      console.error('Error fetching requesters:', error);
      // Don't set error state here as it's not critical
    }
  };

  useEffect(() => {
    fetchModels();
    fetchRequesters();
  }, []);

  // Single debounced search effect that handles all search/filter changes
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue, page) => {
        fetchLogs(page, searchValue);
      }, 150), // Set to 150ms for very responsive searching
    [fetchLogs]
  );

  // Cleanup effect for debounced search
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Single effect to handle all data fetching
  useEffect(() => {
    debouncedSearch(searchPrompt, pagination.page);
    return () => debouncedSearch.cancel();
  }, [searchPrompt, filterModel, filterRequester, pagination.page, pagination.pageSize, debouncedSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchPrompt(value);
    // Reset to page 1 when searching
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };
  // Handle page changes
  const handlePageChange = useCallback((newPage) => {
    // Update pagination state but let the effect handle the fetch
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  return (
    <div className="inference-logs">
      
      <div className="filters">
        <div className="filters-left">
          <div className="filter-group">
            <label>
              Model: 
              <select 
                value={filterModel} 
                onChange={(e) => {
                  setFilterModel(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="all">All Models</option>
                {Object.entries(models)
                  .sort((a, b) => a[1].localeCompare(b[1]))
                  .map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
              </select>
            </label>
          </div>
          <div className="filter-group">
            <label>
              Requester:
              <select
                value={filterRequester}
                onChange={(e) => {
                  setFilterRequester(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="all">All Requesters</option>
                {requesters.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="filter-group">
            <label>
              Prompt:
              <input
                type="text"
                value={searchPrompt}
                onChange={handleSearchChange}
                placeholder="Filter by prompt text..."
              />
            </label>
          </div>
        </div>
      </div>
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <PaginationControls 
            pagination={pagination} 
            onPageChange={handlePageChange} 
          />
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      
      {isLoading ? (
        <div className="loading">Loading logs...</div>
      ) : logs.length === 0 ? (
        <div className="no-results">No logs found</div>
      ) : (
        <>          
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th>Requester</th>
                <th>Status</th>
                <th>Prompt</th>
                <th>Result</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Cost</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const startTime = log.startedAt ? new Date(log.startedAt) : new Date(log.createdAt);
                const endTime = log.completedAt ? new Date(log.completedAt) : null;
                const duration = endTime ? Math.round((endTime - startTime) / 1000) : null;
                
                return (
                <React.Fragment key={log._id}>
                  <tr>
                    <td>{models[log.modelId] || log.modelId}</td>
                    <td>{log.requester || 'Unknown'}</td>
                    <td>
                      <span className={`status-badge status-${log.status}`} title={log.status}>
                        {log.status === 'running' && '⏳'}
                        {log.status === 'completed' && '✅'}
                        {log.status === 'failed' && '❌'}
                        {log.status === 'connection_error' && '⚠️'}
                        {log.status === 'content_policy_violation' && '🚫'}
                      </span>
                    </td>
                    <td>{truncateText(log.prompt, 50)}</td>
                    <td>{renderPreview(log)}</td>
                    <td>{startTime.toLocaleString()}</td>
                    <td>
                      {duration !== null ? `${duration}s` :
                       log.status === 'running' ? 'Running...' : 'N/A'}
                    </td>
                    <td>{formatCost(log)}</td>
                    <td>
                      <button
                        onClick={() => setExpandedLogId(expandedLogId === log._id ? null : log._id)}
                        className="details-button"
                      >
                        {expandedLogId === log._id ? 'Hide Details' : 'Show Details'}
                      </button>
                    </td>
                  </tr>
                  {expandedLogId === log._id && (                    <tr className="details-row">
                      <td colSpan={9}>
                        <div className="log-details">
                          <h4>Identifiers</h4>
                          <pre>{`Requester: ${log.requester || 'N/A'}${log.ipAddress ? ` (IP: ${log.ipAddress})` : ''}
Session ID: ${log.sessionId || 'N/A'}
FAL Request ID: ${log.falRequestId || 'N/A'}`}</pre>
                          <h4>Full Prompt</h4>
                          <pre>{log.prompt}</pre>
                          <InputThumbnails inputs={log.inputs} schema={modelSchemas[log.modelId?.toString()]} />
                          <h4>Full Inputs</h4>
                          <pre>{JSON.stringify(log.inputs, null, 2)}</pre>
                          {(log.status === 'failed' || log.status === 'content_policy_violation') && log.errorDetails && (
                            <>
                              <h4>Error Summary</h4>
                              <div className="error-summary">
                                {log.errorDetails.validationErrors && log.errorDetails.validationErrors.length > 0 ? (
                                  <ul>
                                    {log.errorDetails.validationErrors.map((error, index) => (
                                      <li key={index}>
                                        <strong>{error.loc?.filter(l => l !== 'body').join('.') || 'Field'}:</strong>{' '}
                                        {error.msg}
                                        {error.type && <span className="error-type"> (Type: {error.type})</span>}
                                        {error.input && (
                                          <div className="error-input">
                                            <em>Input provided:</em> {typeof error.input === 'object' ? 
                                              `{${Object.keys(error.input).join(', ')}}` : 
                                              String(error.input)}
                                          </div>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p>{log.errorDetails.message || 'Unknown error occurred'}</p>
                                )}
                              </div>
                              <h4>Full Error Details</h4>
                              <pre className="error-details">{JSON.stringify(log.errorDetails, null, 2)}</pre>
                            </>
                          )}
                          {log.result && (
                            <>
                              <h4>Full Result</h4>
                              <pre>{JSON.stringify(log.result, null, 2)}</pre>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>          </table>
          
          {pagination.totalPages > 1 && (
            <PaginationControls 
              pagination={pagination} 
              onPageChange={handlePageChange} 
            />
          )}
        </>
      )}
    </div>
  );
};

export default InferenceLogs;
